import { authClientWeb } from "react-redux-django-auth/web";

export type NotificationType = "INFO" | "SUCCESS" | "WARNING" | "ALERT";

export interface NotificationItem {
  id: number;
  recipient: number;
  actor?: number | null;
  actor_name?: string | null;
  title: string;
  message: string;
  notification_type: NotificationType;
  is_read: boolean;
  read_at?: string | null;
  target_model?: string | null;
  target_id?: string | null;
  extra_data?: Record<string, any>;
  created_at: string;
}

export interface NotificationQueryParams {
  page?: number;
  page_size?: number;
  is_read?: boolean;
  notification_type?: NotificationType | string;
}

export interface NotificationListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: NotificationItem[];
}

export interface UnreadCountResponse {
  unread_count: number;
}

const getClient = () => {
  try {
    const client = authClientWeb();
    if (client) return client;
  } catch {
    // fallback if authClientWeb is not initialized yet
  }
};

/**
 * Fetch paginated user notifications with optional is_read or type filtering.
 */
export const fetchNotifications = async (
  params: NotificationQueryParams = {}
): Promise<NotificationListResponse> => {
  const client = getClient();
  if (!client) throw new Error("API client not initialized");

  const query = new URLSearchParams();
  if (params.page) query.append("page", params.page.toString());
  if (params.page_size) query.append("page_size", params.page_size.toString());
  if (params.is_read !== undefined) query.append("is_read", params.is_read.toString());
  if (params.notification_type && params.notification_type !== "ALL") {
    query.append("notification_type", params.notification_type);
  }

  const queryString = query.toString();
  const url = queryString ? `/notifications/?${queryString}` : "/notifications/";
  const res = await client.get(url);

  // DRF PageNumberPagination returns { count, next, previous, results }
  // Support both paginated and plain array fallback
  if (Array.isArray(res.data)) {
    return {
      count: res.data.length,
      next: null,
      previous: null,
      results: res.data,
    };
  }

  return {
    count: res.data?.count || 0,
    next: res.data?.next || null,
    previous: res.data?.previous || null,
    results: res.data?.results || [],
  };
};

/**
 * Retrieve single notification details by ID.
 */
export const fetchNotificationById = async (id: number): Promise<NotificationItem> => {
  const client = getClient();
  if (!client) throw new Error("API client not initialized");

  const res = await client.get(`/notifications/${id}/`);
  return res.data;
};

/**
 * Mark an individual notification as read.
 */
export const markNotificationAsRead = async (id: number): Promise<NotificationItem> => {
  const client = getClient();
  if (!client) throw new Error("API client not initialized");

  const res = await client.post(`/notifications/${id}/mark-read/`, {});
  return res.data;
};

/**
 * Bulk-mark all unread notifications for the user as read.
 */
export const markAllNotificationsAsRead = async (): Promise<{ detail: string; updated_count: number }> => {
  const client = getClient();
  if (!client) throw new Error("API client not initialized");

  const res = await client.post("/notifications/mark-all-read/", {});
  return res.data;
};

/**
 * Fetch real-time count of unread notifications.
 */
export const fetchUnreadCount = async (): Promise<number> => {
  const client = getClient();
  if (!client) throw new Error("API client not initialized");

  const res = await client.get("/notifications/unread-count/");
  return res.data?.unread_count ?? 0;
};
